import { FormHandles } from '@unform/core';
import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { InputCheckbox } from '../../../../components/InputCheckbox';
import { Painel } from '../../../../components/Painel';
import { SeparateLabel } from '../../../../components/SeparateLabel';
import { DisabledField } from '../../../../components/ComingSoonTag';
import { useToast } from '../../../../hooks/toast';
import { api } from '../../../../services/api';
import { getValidationErrors } from '../../../../utils/getValidationErrors';

import {
  Card,
  Container,
  Discard,
  ExistingUserTag,
  FormContainer,
  Header,
  Info,
  Input,
  InviteToggleRow,
  PageTitle,
  Row2,
  Submit,
} from './styles';

interface IFormProps {
  fullname: string;
  nickname: string;
  email: string;
}

interface ICheckEmailResponse {
  exists: boolean;
  fullname?: string;
  nickname?: string;
}

const UserInvite: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [existingUser, setExistingUser] = useState(false);
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const handleBlurEmail = useCallback(async () => {
    const email = formRef.current?.getFieldValue('email');

    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      return;
    }

    try {
      setCheckingEmail(true);

      const response = await api.get<ICheckEmailResponse>('users/check-email', {
        params: { email: email.toLowerCase() },
      });

      if (response.data.exists) {
        setExistingUser(true);
        formRef.current?.setFieldValue('fullname', response.data.fullname);
        formRef.current?.setFieldValue('nickname', response.data.nickname);
      } else {
        setExistingUser(false);
      }
    } catch {
      // Falha na checagem não deve travar o preenchimento manual --
      // simplesmente segue como se o email fosse novo.
      setExistingUser(false);
    } finally {
      setCheckingEmail(false);
    }
  }, []);

  const handleSubmit = useCallback(
    async (data: IFormProps) => {
      try {
        setLoading(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          fullname: Yup.string().required('Campo obrigatório'),
          nickname: Yup.string().required('Campo obrigatório'),
          email: Yup.string()
            .email('Insira um email válido')
            .required('Campo obrigatório'),
        });

        await schema.validate(data, { abortEarly: false });

        await api.post('users/invite-user', {
          fullname: data.fullname,
          nickname: data.nickname,
          email: data.email.toLowerCase(),
        });

        addToast({
          type: 'success',
          title: 'Convite enviado',
          description: `Convite enviado com sucesso ao email ${data.email}`,
        });

        setLoading(false);
        navigate('/users');
      } catch (err: any) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }
        if (err?.response?.status === 401) {
          addToast({
            type: 'error',
            title: 'Email já cadastrado',
            description:
              'O email informado ja está cadastrado em nosso sistema.',
          });
          return;
        }
        addToast({
          type: 'error',
          title: 'Ocorreu um erro',
          description:
            'Tente novamente mais tarde. Caso o erro persista, entre em contato com a administração do sistema.',
        });
      }
    },
    [addToast, navigate],
  );

  return (
    <Painel>
      <Container>
        <Header>
          <PageTitle>Adicionar aluno</PageTitle>
          <Discard type="button" onClick={() => navigate('/users')}>
            Voltar
          </Discard>
        </Header>

        <Card>
          <SeparateLabel label="Convite" />
          <Info text="Ao cadastrar os dados, um token será enviado ao email do usuário." />
          <Info text="Esse token deve ser inserido no app “TrenzoX” ou na plataforma web, para finalizar o cadastro." />

          <FormContainer ref={formRef} onSubmit={handleSubmit}>
            <Input label="Email" name="email" onBlur={handleBlurEmail} />

            {checkingEmail && <Info text="Verificando email..." />}

            {existingUser && (
              <ExistingUserTag>
                Esse email já tem cadastro na plataforma. Nome e apelido
                preenchidos automaticamente.
              </ExistingUserTag>
            )}

            <div
              style={
                existingUser
                  ? { opacity: 0.5, pointerEvents: 'none' }
                  : undefined
              }
            >
              <Row2>
                <Input
                  label="Nome completo"
                  name="fullname"
                  readOnly={existingUser}
                />
                <Input
                  label="Apelido"
                  name="nickname"
                  readOnly={existingUser}
                />
              </Row2>
            </div>

            <InviteToggleRow>
              <DisabledField>
                <InputCheckbox name="send_invite_email" defaultChecked />
              </DisabledField>
              <div>
                <p>Enviar convite por e-mail</p>
                <span>
                  O aluno recebe um link para concluir o cadastro e definir a
                  senha.
                </span>
              </div>
            </InviteToggleRow>

            <Submit
              name="submit"
              value="Enviar convite"
              loading={loading}
              disabled={loading}
            />
          </FormContainer>
        </Card>
      </Container>
    </Painel>
  );
};

export { UserInvite };
